import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import { InputGroup, Spinner } from '@blueprintjs/core';
import * as XLSX from 'xlsx';
import { originalQuery } from './api';
import { GridZoomControl } from '../ui/GridZoomControl';
import { useTableZoom } from '../../utils/tableViewSettings';
import { ErrorNote, LEAD_IMPORT_ZOOM, colLetter, fmtInt } from './ui';

/**
 * Read-only view of the original file, for looking only. The server's own
 * reading of the file (sheet list, counts, header checks) is what the import
 * uses; this preview never decides what gets imported.
 *
 * SheetJS (parse) + AG Grid (virtualized DOM grid) rather than a canvas
 * spreadsheet widget: both are already app dependencies, large tabs scroll
 * smoothly, and every part is plain DOM that the li-sheet CSS can style.
 * Cells show their displayed text.
 */

const MAX_ROWS = 20000;

interface SheetLine { n: number; cells: string[] }
interface ParsedSheet { name: string; hidden: boolean; rows: string[][]; width: number; truncated: boolean; totalRows: number }

function parse(buffer: ArrayBuffer, filename: string): ParsedSheet[] {
  const isText = /\.(csv|tsv|txt)$/i.test(filename);
  const wb = XLSX.read(buffer, { type: 'array', dense: true, raw: isText, cellText: true, cellNF: true, sheetRows: MAX_ROWS + 1 });
  const meta = wb.Workbook?.Sheets ?? [];
  return wb.SheetNames.map((name, i) => {
    const ws = wb.Sheets[name];
    const ref = ws['!ref'] ? XLSX.utils.decode_range(ws['!ref']) : null;
    const dense = (ws as unknown as { '!data'?: { w?: string; v?: unknown }[][] })['!data'] ?? [];
    const rows: string[][] = [];
    let width = 0;
    const limit = Math.min(dense.length, MAX_ROWS);
    for (let r = 0; r < limit; r++) {
      const row = dense[r] ?? [];
      const out: string[] = [];
      for (let c = 0; c < row.length; c++) {
        const cell = row[c];
        out[c] = cell ? String(cell.w ?? cell.v ?? '') : '';
      }
      width = Math.max(width, out.length);
      rows.push(out);
    }
    const totalRows = ref ? ref.e.r + 1 : rows.length;
    return { name, hidden: Boolean(meta[i]?.Hidden), rows, width, truncated: totalRows > MAX_ROWS, totalRows };
  });
}

export function SpreadsheetPreview({ buffer, filename, activeSheet, onActiveSheetChange, showTabs = true, height = 460 }: {
  buffer: ArrayBuffer;
  filename: string;
  /** Controlled sheet name; when omitted the preview manages its own tab. */
  activeSheet?: string;
  onActiveSheetChange?: (name: string) => void;
  showTabs?: boolean;
  height?: number | string;
}) {
  const parsed = useMemo(() => {
    try { return { sheets: parse(buffer, filename), error: null as string | null }; }
    catch (e) { return { sheets: [] as ParsedSheet[], error: (e as Error).message }; }
  }, [buffer, filename]);
  const [own, setOwn] = useState(0);
  const controlledIndex = activeSheet != null ? parsed.sheets.findIndex((s) => s.name === activeSheet) : -1;
  const active = controlledIndex >= 0 ? controlledIndex : own;
  const [cell, setCell] = useState<{ ref: string; value: string } | null>(null);
  const [find, setFind] = useState('');
  const [zoom, setZoom] = useTableZoom('lead-import-sheet', LEAD_IMPORT_ZOOM);
  useEffect(() => { setCell(null); }, [active]);

  const sheet = parsed.sheets[active];
  const rowData = useMemo(() => (sheet ? sheet.rows.map((cells, i) => ({ n: i + 1, cells })) : []), [sheet]);
  const columns = useMemo<ColDef<SheetLine>[]>(() => {
    if (!sheet) return [];
    const cols: ColDef<SheetLine>[] = [{
      colId: '__row', headerName: '', width: Math.round(58 * zoom), pinned: 'left', sortable: false, resizable: false,
      valueGetter: (p) => p.data?.n, cellClass: 'li-sheet__rownum',
    }];
    for (let c = 0; c < Math.max(sheet.width, 1); c++) {
      cols.push({ colId: `c${c}`, headerName: colLetter(c), width: Math.round(140 * zoom), sortable: false, valueGetter: (p) => p.data?.cells[c] ?? '', tooltipValueGetter: (p) => p.value });
    }
    return cols;
  }, [sheet, zoom]);

  if (parsed.error) return <ErrorNote error={`This file could not be shown: ${parsed.error}`} />;
  if (!sheet) return <div className="li-empty"><div className="li-empty__text">The file has no sheets.</div></div>;
  const choose = (i: number) => { setOwn(i); onActiveSheetChange?.(parsed.sheets[i].name); };

  return (
    <div className="li-sheet">
      <div className="li-sheet__bar">
        <span className="li-sheet__ref" aria-label="Selected cell">{cell?.ref ?? ''}</span>
        <span className="li-sheet__value" title={cell?.value}>{cell?.value ?? <span className="li-muted">Select a cell to see its full value</span>}</span>
        <InputGroup small leftIcon="search" className="li-sheet__find" value={find} placeholder="Find in this sheet" onChange={(e) => setFind(e.target.value)} aria-label="Find in this sheet" />
        <GridZoomControl zoom={zoom} onChange={setZoom} defaultZoom={LEAD_IMPORT_ZOOM} />
      </div>
      <div className="ag-theme-alpine li-sheet__grid" style={{ height, ['--sky-grid-zoom' as string]: zoom }}>
        <AgGridReact<SheetLine>
          rowData={rowData}
          columnDefs={columns}
          defaultColDef={{ resizable: true, suppressHeaderMenuButton: true }}
          rowHeight={Math.round(26 * zoom)}
          headerHeight={Math.round(26 * zoom)}
          enableCellTextSelection
          getRowClass={(p) => (p.data?.n === 1 ? 'li-sheet__row1' : undefined)}
          tooltipShowDelay={400}
          quickFilterText={find}
          onCellFocused={(e) => {
            if (e.rowIndex == null || !e.column || typeof e.column === 'string') return;
            const colId = e.column.getColId();
            if (colId === '__row') return;
            const c = Number(colId.slice(1));
            const row = e.api.getDisplayedRowAtIndex(e.rowIndex)?.data as SheetLine | undefined;
            if (row) setCell({ ref: `${colLetter(c)}${row.n}`, value: row.cells[c] ?? '' });
          }}
        />
      </div>
      {showTabs && parsed.sheets.length > 1 && (
        <div className="li-sheet__tabs" role="tablist" aria-label="Sheets in the file">
          {parsed.sheets.map((s, i) => (
            <button key={s.name} type="button" role="tab" aria-selected={i === active}
              className={`li-sheet__tab${i === active ? ' is-active' : ''}${s.hidden ? ' is-hidden' : ''}`}
              onClick={() => choose(i)} title={s.hidden ? 'Hidden in the workbook' : undefined}>
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="li-sheet__status">
        <span>{fmtInt(sheet.totalRows)} rows, {sheet.width} columns</span>
        {sheet.truncated && <span>Showing the first {fmtInt(MAX_ROWS)} rows</span>}
        <span className="li-muted">Row 1 is highlighted because it is read as the header.</span>
      </div>
    </div>
  );
}

/** Preview of a batch's stored original, fetched through a short-lived signed URL. */
export function StoredOriginalPreview({ batchId, filename, activeSheet, onActiveSheetChange, showTabs, height }: {
  batchId: number; filename: string; activeSheet?: string; onActiveSheetChange?: (name: string) => void; showTabs?: boolean; height?: number | string;
}) {
  const q = useQuery(originalQuery(batchId));
  if (q.isLoading) return <div className="li-sheet li-sheet--loading"><Spinner size={20} /><span>Loading the file</span></div>;
  if (q.error) return <ErrorNote error={q.error} />;
  return q.data ? <SpreadsheetPreview buffer={q.data} filename={filename} activeSheet={activeSheet}
    onActiveSheetChange={onActiveSheetChange} showTabs={showTabs} height={height} /> : null;
}
